import { useRef, useCallback, useEffect } from "react"
import { useInfiniteQuery, useMutation, useQueryClient, InfiniteData } from "@tanstack/react-query"
import { apiFe } from "@/shared/api/apiFe"
import useAlert from "@/shared/hooks/useAlert"
import { storeFutuUserInfo } from "@/shared/store/userInfo"
import { useTranslations } from 'use-intl';
import { useSession } from "@/app/providers"

export const useNoticeData = (initialData: PostNotificationListRes | null) => {
  const session = useSession()
  // console.log("NoticePage", initialData)
  const { addAlert } = useAlert()
  const observerRef = useRef<IntersectionObserver | null>(null)
  // const router = useRouter()
  const t = useTranslations();

  // 초기 데이터에서 unreadCnt 설정
  useEffect(() => {
    if (initialData?.result_code === '100' && initialData.unread_cnt !== undefined) {
      storeFutuUserInfo.getState().setUnreadCnt(initialData.unread_cnt)
    }
  }, [initialData])

  const fetchNoticeList = async ({ pageParam = 1 }: { pageParam?: number }) => {
    // console.log("API 요청 - pageParam:", pageParam)
    const response = await apiFe.post<PostNotificationListRes>('/notification', {
      pagesize: "50", // 한 번에 가져올 개수
      req_pagenum: pageParam.toString(), // 현재페이지
    })

    // unreadCnt를 스토어에 설정
    storeFutuUserInfo.getState().setUnreadCnt(response.data.unread_cnt)

    const mobid = (typeof window !== 'undefined' && localStorage.getItem('MobID')) || '';
    const userInfoResponse = await apiFe.post("/v1/refreshToken", {}, { headers: { DeviceID: mobid } })

    return response.data
  }


  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch: refetchNotification
  } = useInfiniteQuery({
    queryKey: ['notifications'],
    queryFn: fetchNoticeList,
    initialPageParam: 1,
    initialData: initialData?.result_code === '100' ? {
      pages: [initialData],
      pageParams: [1]
    } : undefined,
    enabled: !!session?.NewAuthToken,
    getNextPageParam: (lastPage, allPages) => {
      // totalpages가 있으면 그것을 기준으로 마지막 페이지 확인
      if (lastPage?.totalpages && allPages.length >= Number(lastPage.totalpages)) {
        // console.log("마지막 페이지 도달 - 더 이상 로드하지 않음")
        return undefined
      }

      // 현재 로드된 페이지 수 + 1을 다음 페이지로 반환
      const nextPage = allPages.length + 1
      // console.log("다음 페이지:", nextPage)
      return nextPage
    },
    staleTime: 5 * 60 * 1000, // 5분
  })

  const queryClient = useQueryClient();

  // notificationVersion 변경 시 알림 리스트 새로고침
  const notificationVersion = storeFutuUserInfo((state) => state.notificationVersion)

  useEffect(() => {
    if (notificationVersion > 0) {
      refetchNotification()
    }
  }, [notificationVersion, refetchNotification])
  // 무한 스크롤을 위한 Intersection Observer
  const lastElementRef = useCallback((node: HTMLElement | null) => {
    if (isLoading) return

    if (observerRef.current) observerRef.current.disconnect()

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    })

    if (node && observerRef.current) observerRef.current.observe(node)
  }, [isLoading, hasNextPage, isFetchingNextPage, fetchNextPage])

  // 알림 삭제 API 설정
  const { mutate: deleteNoti } = useMutation({
    mutationFn: async () => {
      const res = await apiFe.delete("/notification")
      return res.data
    },
    onSuccess: () => {
      addAlert({ message: t('Alert.Notice.allDelete') })
      // 전체삭제 후 모든 탭 데이터 refetch
      refetchNotification()
    },
    onError: () => {
      addAlert({ message: t('Alert.Notice.retry') })
    },
  })

  // 알림 읽음 상태를 변경하는 함수
  const putNotification = async (id_noti?: string) => {
    try {
      const res = await apiFe.put(`/notification`, {
        id_noti, // 클릭한 알림 id
      });
      console.log("putNotification response:", res.data);
      return res.data;
    } catch (error) {
      console.error("putNotification API 호출 중 오류 발생:", error);
      return null;
    }
  };

  // 모두 삭제
  const handleDeleteAll = () => {
    deleteNoti()
    console.log("모두 삭제")
  }

  //React Query 캐시만 모두 읽음 처리
  const updateAllReadInCache = () => {
    queryClient.setQueryData<InfiniteData<PostNotificationListRes, number>>(
      ['notifications'],
      (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            unread_cnt: 0,
            list: (p.list ?? []).map((it) => ({ ...it, yn_read: 'Y' as const })),
          })),
        };
      }
    );
    storeFutuUserInfo.getState().setUnreadCnt(0);
  };


  // 모두 읽음 처리
  const handleReadAll = () => {

    // 안읽은 알람 개수 체크
    const hasUnread =
      allNotifications.some((item) => item.yn_read === 'N');

    if (!hasUnread) {
      addAlert({ message: t('Alert.Notice.allRead') });
      return;
    }


    putNotification().then((res) => {
      if (res?.result_code === '100') {
        updateAllReadInCache();
        refetchNotification();
      } else {
        addAlert({ message: t('Alert.Notice.readFail') });
      }
    });
  };

  // 하나만 읽음 처리
  const handleReadOne = (item: PostNotificationList) => {
    if (item.yn_read === 'Y') return
    putNotification(item.id_noti).then((res) => {
      if (res?.result_code === '100') {
        refetchNotification()
      } else {
        addAlert({ message: t('Alert.Notice.readFail') })
      }
    })
  }

  const allNotifications = data?.pages.flatMap(page => page.list || []) || []

  useEffect(() => {
    if (initialData?.result_code !== '100') {
      const resultMsg = initialData?.msg.replaceAll('[null]', '')
      addAlert({
        title: t('Alert.Notice.listFail'),
        message: resultMsg,
        onConfirm: () => { }
      })
    }
  }, [initialData?.result_code, initialData?.msg, addAlert])

  useEffect(() => {
    const unreadCnt = storeFutuUserInfo.getState().unreadCnt
    if (Number(unreadCnt) > 0) {
      // 추후 invalidateQueries 예정
    }
  }, [])

  return {
    allNotifications,
    isLoading,
    isFetchingNextPage,
    lastElementRef,
    handleDeleteAll,
    handleReadAll,
    handleReadOne,
    unreadCnt: storeFutuUserInfo.getState().unreadCnt,
    refetchNotification
  }
}
